"use client";

import React, { ReactNode } from 'react';
import PageTitle from './PageTitle/PageTitle';
import CustomLinkButton from './CustomLinkButton';

import LayoutButtonWrapper from "@/components/LayoutButtonWrapper";
import FullscreenSpinner from "@/components/Spinner/FullscreenSpinner";

import { ResponsiveGap } from './ResponsiveGap';
import { useIsUnsupported } from '@/utils/useIsUnsupported';
import { useResponsiveClass } from '@/utils/useResponsiveClass';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { ArrowLeft } from '$/ArrowLeft';
import { colors, fonts, themeColors } from '@/stylingConstants';
import Link from 'next/link';

type LayoutProps = {
    children: ReactNode,
    showFooter?:boolean,
    showMobileFooterBar?:boolean,
    pageTitle: string,
    backUrl?: string | null,
    nextUrl?: string | null,
    nextUrlLabel?: string,
    contentStyle?: {
        [key: string]: string | number;
    }
    customNextButtons?: React.ReactNode[],
    hideTitle?: boolean,
    displayHeader?: boolean,
    showSpinner?: boolean,
    analyticsBackLabel?: string
    containerRef?:React.RefObject<HTMLDivElement | null>
    customStyle?: React.CSSProperties,
    customFooterStyles?: React.CSSProperties,
    marginTop?:string,
    disableTranslation?:boolean,
    showBackBanner?: boolean,
    backBannerLabel?: string,
    onBackBannerClick?: () => void,
    bannerStepText?: string,
}

const Layout = ({
    children,
    pageTitle,
    backUrl,
    nextUrl,
    nextUrlLabel,
    showFooter=true,
    showMobileFooterBar=false,
    contentStyle = {},
    hideTitle = false,
    displayHeader=true,
    showSpinner=false,
    customNextButtons,
    analyticsBackLabel,
    containerRef,
    customStyle={},
    customFooterStyles={},
    marginTop='mt-[64px]',
    disableTranslation=false,
    showBackBanner=false,
    backBannerLabel='Back',
    onBackBannerClick,
    bannerStepText
}: LayoutProps) => {

    const contentWrapper = React.useRef<HTMLDivElement>(null);
    const { isUnsupported } = useIsUnsupported();
    const getResponsiveClass = useResponsiveClass();
    const { isMobile } = getResponsiveClass;
    const isC1Integrated = useIsC1Integrated();

    return (
        <div className={'container relative overflow-x-hidden' + 
        ( isUnsupported ? ' unsupportedDeviceMode' : '' )+
        (disableTranslation ? ' notranslate' : '')
        } ref={containerRef} 
        style={customStyle}
        {...(disableTranslation ? { translate: "no" } : {})}>
            <FullscreenSpinner visible={showSpinner} />
            {displayHeader && !isC1Integrated &&<header className="header" ref={contentWrapper}>
                {hideTitle || <div className="bg-gray-200 w-full sticky top-0 z-10 ">
                    <PageTitle title={pageTitle} backUrl={backUrl} analyticsBackLabel={analyticsBackLabel} />
                </div>}
            </header>}
            {showBackBanner && (
                <div
                    style={{
                        width: '100%',
                        boxShadow: '0px 1px 15px -2px rgba(0, 0, 0, 0.1)',
                        height: isMobile ? '60px' : '84px',
                        alignContent: 'center'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: isMobile ? '0' : '0 64px'
                        }}
                    >
                        {/* Back button link */}
                        <Link
                            href={backUrl ?? '#'}
                            onClick={(event) => {
                                event.preventDefault();
                                onBackBannerClick?.();
                            }}
                            aria-label="Back"
                            style={{
                                background: 'none',
                                border: 'none',
                                color: isC1Integrated ? themeColors.adult.cupMainColor1 : colors.CambridgeBlue_50,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '12px 0',
                                paddingLeft: isMobile ? '24px' : '32px',
                                paddingRight: '16px',
                                cursor: 'pointer',
                                ...fonts.bodysm,
                                fontWeight: 600,
                                flexShrink: 0
                            }}
                        >
                            <ArrowLeft />
                            {!bannerStepText && backBannerLabel}
                        </Link>
                        {(bannerStepText) && (
                            <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 0' }}>
                                {bannerStepText && (
                                    <div style={{ 
                                        ...fonts.bodyxs, 
                                        color: themeColors.adult.cupGeneralColor5
                                    }}>
                                        {bannerStepText}
                                    </div>
                                )}
                                <div style={{ 
                                    ...fonts.headerxs,
                                    fontWeight: '600',
                                    color: themeColors.adult.cupGeneralColor5
                                }}>
                                    {backBannerLabel}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <main className="content custom-scrollbar" style={contentStyle}>
                
                {children}
                {showMobileFooterBar ? <div style={{ height: '24px' }}></div> : <ResponsiveFooter customNextButtons={customNextButtons} marginTop={marginTop} isMobile={isMobile} />}
            </main>
            {(nextUrl || customNextButtons) && showFooter && (showMobileFooterBar || isMobile) && (
                <footer className="footer" 
                style={{
                    boxShadow: '0px -2px 24px -6px rgba(0, 0, 0, 0.4)',
                    ...customFooterStyles
                }}
                >
                    <LayoutButtonWrapper>
                        {nextUrl && 
                            <CustomLinkButton
                                href={nextUrl}>{nextUrlLabel ?? 'OK'}
                            </CustomLinkButton>
                            }
                        {customNextButtons}
                    </LayoutButtonWrapper>
                </footer>
            )}
        </div>
    );

};

const ResponsiveFooter = ({ customNextButtons, marginTop, isMobile }: { readonly customNextButtons?: React.ReactNode[], readonly marginTop: string, readonly isMobile: boolean }) => {
    const validButtons = customNextButtons?.filter(Boolean) || [];
    return validButtons.length ? (
        <div style={{ gridColumn: '1/-1' }}>
            <ResponsiveGap className={marginTop} />
            {!isMobile &&
                <div style={{ display: 'flex' }}>
                    <div style={{ marginRight: validButtons.length > 1 ? 16 : 0 }}>
                        {customNextButtons?.[0]}
                    </div>
                    <span>{customNextButtons?.[1]}</span>
                    <ResponsiveGap className='mb-[80px]' />
                </div>}
        </div>
    ) :
        <div style={{ height: '24px' }}></div>;
}

export default Layout;